import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import { useAuthStore } from './stores/auth';
import { AdminLayout } from './components/layout/AdminLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { LicensesPage } from './pages/licenses/LicensesPage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { CustomerReportsPage } from './pages/customers/CustomerReportsPage';
import { ActivationsPage } from './pages/activations/ActivationsPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { AuditLogPage } from './pages/audit/AuditLogPage';
import { UsersPage } from './pages/users/UsersPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { UpdatePasswordPage } from './pages/settings/UpdatePasswordPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, _hasHydrated } = useAuthStore();

  if (!_hasHydrated) {
    return <LoadingOverlay visible />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

interface AdminOnlyRouteProps {
  children: React.ReactNode;
}

function AdminOnlyRoute({ children }: AdminOnlyRouteProps) {
  const { user } = useAuthStore();

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: React.ReactNode;
}

function PublicRoute({ children }: PublicRouteProps) {
  const { isAuthenticated, _hasHydrated } = useAuthStore();

  if (!_hasHydrated) {
    return <LoadingOverlay visible />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Placeholder component for routes not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div style={{ padding: '2rem' }}>
      <h1>{title}</h1>
      <p style={{ color: '#666' }}>This page is coming soon.</p>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Reset password - accessible without auth */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected routes with admin layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<DashboardPage />} />

          {/* License Management */}
          <Route path="licenses" element={<LicensesPage />} />
          <Route path="licenses/:id" element={<PlaceholderPage title="License Detail" />} />

          {/* Customer Management */}
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:customerId/reports" element={<CustomerReportsPage />} />
          <Route path="customers/:id" element={<PlaceholderPage title="Customer Detail" />} />

          {/* Activations */}
          <Route path="activations" element={<ActivationsPage />} />
          <Route path="activations/:id" element={<PlaceholderPage title="Activation Detail" />} />

          {/* Products */}
          <Route path="products" element={<ProductsPage />} />

          {/* Users - Admin only */}
          <Route path="users" element={<AdminOnlyRoute><UsersPage /></AdminOnlyRoute>} />

          {/* Audit Log - Admin only */}
          <Route path="audit-log" element={<AdminOnlyRoute><AuditLogPage /></AdminOnlyRoute>} />

          {/* Settings & Profile */}
          <Route path="settings" element={<UpdatePasswordPage />} />
          <Route path="profile" element={<ProfilePage />} />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
